import type { GuidedSetupStep } from '../components/guided-setup/guided-setup-steps';

/**
 * Guided-tour steps for **Overview → PR scans** (`/pr-scans`).
 *
 * Lives beside the other `*-tour-steps.ts` files in `pages/` so the registry's import list stays flat.
 *
 * **Every step is anchored** (a `data-tour-step` ATTRIBUTE on a real element of the list page). The
 * registry claims `/pr-scans` segment-wise, which also covers every route under it (the two scan detail
 * pages and the two Waivers pages); none of
 * these anchors render there, so the host's anchor watcher neither opens the tour on a detail page nor
 * marks it seen — a user who arrives from an SCM status link is not shown a tour of a list they are
 * not looking at.
 *
 * Copy discipline: nothing here asserts a state the platform cannot observe. A team-scoped member sees
 * only the runs of their workspaces, and the copy says so rather than implying the list is complete.
 *
 * Host-owned (`GuidedTourHostComponent`): its permanent "Show me around" trigger is the on-demand
 * re-open path (`.claude/rules/new-section-onboarding-coverage.md` R2), so the page renders no trigger
 * of its own.
 */

/** Tour id — must appear in `UserTourController.ALLOWED_TOUR_IDS` (<= 32 chars). */
export const PR_SCANS_TOUR_ID = 'pr-scans';

export const PR_SCANS_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'prs-list',
    title: 'One row per pull request',
    anchor: 'pr-scans-list',
    body: [
      'Every pull request Phoenix scanned for your organization appears once, newest activity first. ' +
        'Webhook, Jenkins and CI/CLI scans of the same pull request share one row.',
      'The result shown is the newest scan, even when it is still queued or running, or did not ' +
        'finish — an older PASS is never shown as the current result. You see the scans of the ' +
        'workspaces your role covers; scans outside them are not listed.',
    ],
  },
  {
    id: 'prs-filters',
    title: 'Narrow the list',
    anchor: 'pr-scans-filters',
    body: [
      'Filter by provider or repository, or by the newest scan’s result. The result filter looks ' +
        'at each pull request’s newest scan only.',
    ],
  },
  {
    id: 'prs-history',
    title: 'Every scan of a pull request',
    anchor: 'pr-scans-history',
    body: [
      'Click a pull request to expand the table of all its scans, newest first. Each one opens ' +
        'its own detail page; click the row again to collapse it.',
    ],
  },
  {
    // hook-pr plan Task 16. Anchored on the Waivers tab, which renders only for an org with the
    // `approval` entitlement and a caller holding `approval.read` (or as a locked tab when the plan
    // locks `approval`); `requiresFlag` removes the step
    // for every other org, and the absent-anchor skip covers a caller without the permission.
    id: 'prs-waivers',
    title: 'Waivers',
    anchor: 'pr-scans-waivers-tab',
    requiresFlag: 'approval',
    body: [
      'A waiver lets blocked SAST findings through on one branch after an approver agrees with the reason given. ' +
        'When the hook blocks, it prints the command that asks for one.',
      'Waivers lists every request with its findings, who decided it, when it expires, and the scans it let ' +
        'through. An approved waiver stops applying when it expires or is revoked, when its pull request closes ' +
        '(GitHub), or when the code it covers changes.',
    ],
  },
  {
    id: 'prs-refresh',
    title: 'Refresh',
    anchor: 'pr-scans-refresh',
    body: [
      'The list does not update by itself. Refresh to pick up scans that started after the page loaded.',
    ],
  },
];
